import BpmnModdle from 'bpmn-moddle';
import { estimateLabelBounds } from '../core/shapes/textUtils';

// Map epx-flow node types back to BPMN types
const BPMN_TYPE_MAP = {
  'epx:StartEvent': 'bpmn:StartEvent',
  'epx:EndEvent': 'bpmn:EndEvent',
  'epx:UserTask': 'bpmn:UserTask',
  'epx:ServiceTask': 'bpmn:ServiceTask',
  'epx:ScriptTask': 'bpmn:ScriptTask',
  'epx:ManualTask': 'bpmn:ManualTask',
  'epx:SendTask': 'bpmn:SendTask',
  'epx:ReceiveTask': 'bpmn:ReceiveTask',
  'epx:BusinessRuleTask': 'bpmn:BusinessRuleTask',
  'epx:CallActivity': 'bpmn:CallActivity',
  'epx:ExclusiveGateway': 'bpmn:ExclusiveGateway',
  'epx:ParallelGateway': 'bpmn:ParallelGateway',
  'epx:InclusiveGateway': 'bpmn:InclusiveGateway',
  'epx:ComplexGateway': 'bpmn:ComplexGateway',
  'epx:EventBasedGateway': 'bpmn:EventBasedGateway',
  'epx:IntermediateEvent': 'bpmn:IntermediateCatchEvent',
  'epx:IntermediateThrowEvent': 'bpmn:IntermediateThrowEvent',
  'epx:BoundaryEvent': 'bpmn:BoundaryEvent',
  'epx:SubProcess': 'bpmn:SubProcess',
  'epx:Transaction': 'bpmn:Transaction',
  'epx:SequenceFlow': 'bpmn:SequenceFlow',
  'epx:DataObjectReference': 'bpmn:DataObjectReference',
  'epx:DataStoreReference': 'bpmn:DataStoreReference',
  'epx:Group': 'bpmn:Group',
  'epx:TextAnnotation': 'bpmn:TextAnnotation',
};

// Default shape sizes per type
const SHAPE_SIZES = {
  'bpmn:StartEvent': { width: 36, height: 36 },
  'bpmn:EndEvent': { width: 36, height: 36 },
  'bpmn:UserTask': { width: 120, height: 60 },
  'bpmn:ServiceTask': { width: 120, height: 60 },
  'bpmn:ScriptTask': { width: 120, height: 60 },
  'bpmn:ManualTask': { width: 120, height: 60 },
  'bpmn:SendTask': { width: 120, height: 60 },
  'bpmn:ReceiveTask': { width: 120, height: 60 },
  'bpmn:BusinessRuleTask': { width: 120, height: 60 },
  'bpmn:CallActivity': { width: 120, height: 60 },
  'bpmn:ExclusiveGateway': { width: 60, height: 60 },
  'bpmn:ParallelGateway': { width: 60, height: 60 },
  'bpmn:InclusiveGateway': { width: 60, height: 60 },
  'bpmn:ComplexGateway': { width: 60, height: 60 },
  'bpmn:EventBasedGateway': { width: 60, height: 60 },
  'bpmn:IntermediateCatchEvent': { width: 36, height: 36 },
  'bpmn:IntermediateThrowEvent': { width: 36, height: 36 },
  'bpmn:BoundaryEvent': { width: 30, height: 30 },
  'bpmn:SubProcess': { width: 140, height: 80 },
  'bpmn:Transaction': { width: 140, height: 80 },
  'bpmn:DataObjectReference': { width: 36, height: 50 },
  'bpmn:DataStoreReference': { width: 50, height: 50 },
  'bpmn:Group': { width: 300, height: 200 },
  'bpmn:TextAnnotation': { width: 100, height: 40 },
};

export class BpmnWriter {
  constructor() {
    this._moddleExtensions = {};
    this._writerPlugins = [];
    this._moddle = null; // lazily built
  }

  /**
   * Register a moddle extension descriptor.
   * Must be called before write().
   * @param {string} name
   * @param {object} descriptor
   */
  addModdleExtension(name, descriptor) {
    this._moddleExtensions[name] = descriptor;
    this._moddle = null;
  }

  /**
   * Register a writer plugin that can write custom properties to BPMN elements.
   * Plugin signature: { applyProperties(el, props, moddle) → void, applyEdgeProperties?(el, props, moddle) → void }
   * @param {object} plugin
   */
  addWriterPlugin(plugin) {
    this._writerPlugins.push(plugin);
  }

  _getModdle() {
    if (!this._moddle) {
      this._moddle = new BpmnModdle(this._moddleExtensions);
    }
    return this._moddle;
  }

  async write(graphData) {
    const { nodes = [], edges = [], meta = {} } = graphData;
    const warnings = [];

    // Artifact types that go into process.artifacts instead of flowElements
    const ARTIFACT_TYPES = new Set(['epx:Group', 'epx:TextAnnotation']);
    // Data element types that go into flowElements
    const DATA_TYPES = new Set(['epx:DataObjectReference', 'epx:DataStoreReference']);

    // Separate participant/lane nodes from flow nodes and artifacts
    const participantNodes = nodes.filter(n => n.type === 'epx:Participant');
    const laneNodes = nodes.filter(n => n.type === 'epx:Lane');
    const artifactNodes = nodes.filter(n => ARTIFACT_TYPES.has(n.type));
    const flowNodes = nodes.filter(n =>
      n.type !== 'epx:Participant' && n.type !== 'epx:Lane' && !ARTIFACT_TYPES.has(n.type)
    );

    // Separate edge types
    const sequenceFlowEdges = edges.filter(e => e.type === 'epx:SequenceFlow');
    const messageFlowEdges = edges.filter(e => e.type === 'epx:MessageFlow');
    const associationEdges = edges.filter(e =>
      e.type === 'epx:Association' || e.type === 'epx:DataInputAssociation' || e.type === 'epx:DataOutputAssociation'
    );

    // Ensure moddle is built with all registered extensions
    this._getModdle();
    const definitions = this._moddle.create('bpmn:Definitions', {
      // Preserve original targetNamespace and id for round-trip fidelity with sfd-v1
      targetNamespace: meta.targetNamespace || 'http://www.epx.com/schema/1.0',
      id: meta.definitionsId || 'Definitions_1',
    });

    const elementsById = {};

    if (participantNodes.length > 0) {
      // Collaboration mode
      this._writeCollaboration(
        definitions, participantNodes, laneNodes, flowNodes,
        sequenceFlowEdges, messageFlowEdges, artifactNodes, associationEdges,
        elementsById, warnings, meta
      );
    } else {
      // Simple process mode (no pools)
      this._writeSimpleProcess(
        definitions, flowNodes, sequenceFlowEdges, artifactNodes, associationEdges,
        elementsById, warnings, meta
      );
    }

    // Build BPMNDiagram with shape/edge positions
    const allNodes = [...participantNodes, ...laneNodes, ...flowNodes, ...artifactNodes];
    const allEdges = [...sequenceFlowEdges, ...messageFlowEdges, ...associationEdges];
    const diagram = this._buildDiagram(allNodes, allEdges, elementsById, definitions);
    definitions.diagrams = [diagram];

    const { xml } = await this._moddle.toXML(definitions, { format: true });
    return { xml, warnings };
  }

  _writeSimpleProcess(definitions, flowNodes, sequenceFlowEdges, artifactNodes, associationEdges, elementsById, warnings, meta) {
    // Preserve original process ID and sf: properties if available
    const procMeta = (meta && meta.standaloneProcesses && meta.standaloneProcesses[0]) || {};
    const process = this._moddle.create('bpmn:Process', {
      id: procMeta.id || 'Process_1',
      isExecutable: true,
    });
    if (procMeta.props && Object.keys(procMeta.props).length > 0) {
      for (const plugin of this._writerPlugins) {
        if (typeof plugin.applyProcessProperties === 'function') {
          plugin.applyProcessProperties(process, procMeta.props, this._moddle);
        }
      }
    }

    const flowElements = [];
    this._writeFlowNodes(flowNodes, flowElements, elementsById, warnings);
    this._writeSequenceFlows(sequenceFlowEdges, flowElements, elementsById, warnings);

    process.flowElements = flowElements;

    // Write artifacts (Group, TextAnnotation) and associations
    const artifacts = [];
    this._writeArtifacts(artifactNodes, artifacts, elementsById, warnings);
    this._writeAssociations(associationEdges, artifacts, elementsById, warnings);
    if (artifacts.length) {
      process.artifacts = artifacts;
    }

    definitions.rootElements = [process];
  }

  _writeCollaboration(
    definitions, participantNodes, laneNodes, flowNodes,
    sequenceFlowEdges, messageFlowEdges, artifactNodes, associationEdges,
    elementsById, warnings, meta
  ) {
    // Preserve original collaboration ID and sf: properties (guid, code, etc.) if available
    const collabMeta = (meta.collaborations && meta.collaborations[0]) || {};
    const collaboration = this._moddle.create('bpmn:Collaboration', {
      id: collabMeta.id || 'Collaboration_1',
    });
    if (collabMeta.props) {
      this._applyProperties(collaboration, collabMeta.props, 'epx:Collaboration');
    }

    const participants = [];
    const processes = [];

    for (const pNode of participantNodes) {
      const pProps = pNode.properties || {};
      const processId = pProps.processRef || `Process_${pNode.id}`;
      const process = this._moddle.create('bpmn:Process', {
        id: processId,
        isExecutable: true,
        // Process name mirrors participant name (same as sfd-v1 behavior)
        name: pProps.name || pNode.text || '',
      });

      // Apply process-level sf: properties (guid, code, version, etc.) via plugins
      const processProps = pProps._processProps;
      if (processProps) {
        for (const plugin of this._writerPlugins) {
          if (typeof plugin.applyProcessProperties === 'function') {
            plugin.applyProcessProperties(process, processProps, this._moddle);
          }
        }
      }

      // Determine which flow nodes belong to this participant by proximity
      // (nodes whose center falls within the participant's bounding box)
      const pW = pProps.width || 600;
      const pH = pProps.height || 250;
      const pLeft = pNode.x - pW / 2;
      const pRight = pNode.x + pW / 2;
      const pTop = pNode.y - pH / 2;
      const pBottom = pNode.y + pH / 2;

      const containedNodes = flowNodes.filter(n => {
        return n.x >= pLeft && n.x <= pRight && n.y >= pTop && n.y <= pBottom;
      });

      const containedNodeIds = new Set(containedNodes.map(n => n.id));
      const containedEdges = sequenceFlowEdges.filter(e =>
        containedNodeIds.has(e.sourceNodeId) || containedNodeIds.has(e.targetNodeId)
      );

      const flowElements = [];
      this._writeFlowNodes(containedNodes, flowElements, elementsById, warnings);
      this._writeSequenceFlows(containedEdges, flowElements, elementsById, warnings);

      // Write lanes within this pool
      const containedLanes = laneNodes.filter(ln => {
        const lProps = ln.properties || {};
        const lW = lProps.width || 570;
        const lH = lProps.height || 125;
        const lCx = ln.x;
        const lCy = ln.y;
        return lCx >= pLeft && lCx <= pRight && lCy >= pTop && lCy <= pBottom;
      });

      if (containedLanes.length > 0) {
        const laneSet = this._moddle.create('bpmn:LaneSet', { id: `LaneSet_${pNode.id}` });
        const lanes = [];

        for (const lNode of containedLanes) {
          const lProps = lNode.properties || {};
          const lW = lProps.width || 570;
          const lH = lProps.height || 125;
          const lLeft = lNode.x - lW / 2;
          const lRight = lNode.x + lW / 2;
          const lTop = lNode.y - lH / 2;
          const lBottom = lNode.y + lH / 2;

          // Find flow nodes within this lane
          const laneFlowNodeRefs = containedNodes
            .filter(n => n.x >= lLeft && n.x <= lRight && n.y >= lTop && n.y <= lBottom)
            .map(n => elementsById[n.id])
            .filter(Boolean);

          const lane = this._moddle.create('bpmn:Lane', {
            id: lNode.id,
            name: lProps.name || lNode.text || '',
            flowNodeRef: laneFlowNodeRefs,
          });

          // Apply lane-level sf: properties (guid, etc.) via plugins
          this._applyProperties(lane, lProps, 'epx:Lane');

          elementsById[lNode.id] = lane;
          lanes.push(lane);
        }

        laneSet.lanes = lanes;
        process.laneSets = [laneSet];
      }

      process.flowElements = flowElements;

      // Write artifacts for this pool (artifacts whose center falls within the pool)
      const containedArtifactNodes = artifactNodes.filter(n => {
        return n.x >= pLeft && n.x <= pRight && n.y >= pTop && n.y <= pBottom;
      });
      const containedAssocEdges = associationEdges.filter(e => {
        const srcInPool = containedNodeIds.has(e.sourceNodeId) ||
          containedArtifactNodes.some(a => a.id === e.sourceNodeId);
        const tgtInPool = containedNodeIds.has(e.targetNodeId) ||
          containedArtifactNodes.some(a => a.id === e.targetNodeId);
        return srcInPool || tgtInPool;
      });
      const poolArtifacts = [];
      this._writeArtifacts(containedArtifactNodes, poolArtifacts, elementsById, warnings);
      this._writeAssociations(containedAssocEdges, poolArtifacts, elementsById, warnings);
      if (poolArtifacts.length) {
        process.artifacts = poolArtifacts;
      }

      processes.push(process);

      const participant = this._moddle.create('bpmn:Participant', {
        id: pNode.id,
        name: pProps.name || pNode.text || '',
        processRef: process,
      });

      // Apply participant-level sf: properties (guid, code, etc.) via plugins
      this._applyProperties(participant, pProps, 'epx:Participant');

      elementsById[pNode.id] = participant;
      participants.push(participant);
    }

    // MessageFlows
    const messageFlows = [];
    for (const mfEdge of messageFlowEdges) {
      const sourceEl = elementsById[mfEdge.sourceNodeId];
      const targetEl = elementsById[mfEdge.targetNodeId];
      if (!sourceEl || !targetEl) {
        warnings.push(`MessageFlow ${mfEdge.id}: missing source/target`);
        continue;
      }

      const mf = this._moddle.create('bpmn:MessageFlow', {
        id: mfEdge.id,
        name: mfEdge.text || '',
        sourceRef: sourceEl,
        targetRef: targetEl,
      });
      elementsById[mfEdge.id] = mf;
      messageFlows.push(mf);
    }

    collaboration.participants = participants;
    collaboration.messageFlows = messageFlows.length ? messageFlows : undefined;

    definitions.rootElements = [collaboration, ...processes];
  }

  _writeFlowNodes(nodes, flowElements, elementsById, warnings) {
    for (const node of nodes) {
      const bpmnType = BPMN_TYPE_MAP[node.type];
      if (!bpmnType) {
        warnings.push(`Unknown node type: ${node.type}`);
        continue;
      }

      const el = this._moddle.create(bpmnType, {
        id: node.id,
        name: (node.properties || {}).name || (typeof node.text === 'string' ? node.text : (node.text && node.text.value)) || '',
      });

      this._applyProperties(el, node.properties || {}, node.type);
      this._applyEventDefinitions(el, node.properties || {}, bpmnType);
      this._applyLoopCharacteristics(el, node.properties || {});

      // Write triggeredByEvent for Event Sub-Process
      if (bpmnType === 'bpmn:SubProcess' && (node.properties || {}).triggeredByEvent) {
        el.triggeredByEvent = true;
      }

      // Write attachedToRef for BoundaryEvent (resolved in second pass below)
      if (bpmnType === 'bpmn:BoundaryEvent') {
        el._pendingAttachedToRef = (node.properties || {}).attachedToRef;
      }

      flowElements.push(el);
      elementsById[node.id] = el;
    }

    // Second pass: resolve BoundaryEvent attachedToRef now that all hosts are registered
    for (const el of flowElements) {
      if (el._pendingAttachedToRef) {
        const hostEl = elementsById[el._pendingAttachedToRef];
        if (hostEl) {
          el.attachedToRef = hostEl;
        }
        delete el._pendingAttachedToRef;
      }
    }
  }

  _writeSequenceFlows(edges, flowElements, elementsById, warnings) {
    for (const edge of edges) {
      const sourceEl = elementsById[edge.sourceNodeId];
      const targetEl = elementsById[edge.targetNodeId];
      if (!sourceEl || !targetEl) {
        warnings.push(`Edge ${edge.id}: missing source/target`);
        continue;
      }

      const flow = this._moddle.create('bpmn:SequenceFlow', {
        id: edge.id,
        name: (typeof edge.text === 'string' ? edge.text : (edge.text && edge.text.value)) || '',
        sourceRef: sourceEl,
        targetRef: targetEl,
      });

      const props = edge.properties || {};
      this._applyEdgeProperties(flow, props);

      // Write conditionExpression as bpmn:FormalExpression
      if (props.conditionExpression) {
        flow.conditionExpression = this._moddle.create('bpmn:FormalExpression', {
          body: props.conditionExpression,
        });
      }

      // Set the source element's `default` attribute if this is a default flow
      if (props.isDefault === true || props.isDefault === 'true') {
        if (sourceEl) {
          sourceEl.default = flow;
        }
      }

      flowElements.push(flow);
      elementsById[edge.id] = flow;
    }
  }

  _writeArtifacts(artifactNodes, artifacts, elementsById, warnings) {
    for (const node of artifactNodes) {
      const props = node.properties || {};

      if (node.type === 'epx:Group') {
        const group = this._moddle.create('bpmn:Group', {
          id: node.id,
        });
        if (props.name) {
          group.categoryValueRef = props.name;
        }
        this._applyProperties(group, props, 'epx:Group');
        elementsById[node.id] = group;
        artifacts.push(group);
      } else if (node.type === 'epx:TextAnnotation') {
        const annotation = this._moddle.create('bpmn:TextAnnotation', {
          id: node.id,
          text: node.text || props.text || '',
        });
        this._applyProperties(annotation, props, 'epx:TextAnnotation');
        elementsById[node.id] = annotation;
        artifacts.push(annotation);
      }
    }
  }

  _writeAssociations(associationEdges, artifacts, elementsById, warnings) {
    for (const edge of associationEdges) {
      const sourceEl = elementsById[edge.sourceNodeId];
      const targetEl = elementsById[edge.targetNodeId];
      if (!sourceEl || !targetEl) {
        warnings.push(`Association ${edge.id}: missing source/target`);
        continue;
      }

      if (edge.type === 'epx:Association') {
        const assoc = this._moddle.create('bpmn:Association', {
          id: edge.id,
          sourceRef: sourceEl,
          targetRef: targetEl,
          associationDirection: (edge.properties || {}).associationDirection || 'None',
        });
        this._applyEdgeProperties(assoc, edge.properties || {});
        elementsById[edge.id] = assoc;
        artifacts.push(assoc);
      }
      // DataInputAssociation and DataOutputAssociation are written as part of the
      // target/source activity element, not as standalone artifacts. For now, register
      // them in elementsById for diagram DI positioning.
      if (edge.type === 'epx:DataInputAssociation' || edge.type === 'epx:DataOutputAssociation') {
        elementsById[edge.id] = { id: edge.id };
      }
    }
  }

  _applyProperties(el, props, epxType) {
    // Call registered writer plugins for custom property serialization
    for (const plugin of this._writerPlugins) {
      if (typeof plugin.applyProperties === 'function') {
        plugin.applyProperties(el, props, this._moddle);
      }
    }
  }

  _applyEventDefinitions(el, props, bpmnType) {
    const EVENT_TYPES = [
      'bpmn:StartEvent', 'bpmn:EndEvent',
      'bpmn:IntermediateCatchEvent', 'bpmn:IntermediateThrowEvent',
      'bpmn:BoundaryEvent',
    ];
    if (!EVENT_TYPES.includes(bpmnType)) return;

    // Write cancelActivity for non-interrupting events
    if (bpmnType === 'bpmn:BoundaryEvent' || bpmnType === 'bpmn:StartEvent') {
      if (props.isInterrupting === false) {
        el.cancelActivity = false;
      }
    }

    const trigger = props.trigger;
    if (!trigger) return;

    const TRIGGER_DEF_MAP = {
      Timer: 'bpmn:TimerEventDefinition',
      Message: 'bpmn:MessageEventDefinition',
      Signal: 'bpmn:SignalEventDefinition',
      Error: 'bpmn:ErrorEventDefinition',
      Escalation: 'bpmn:EscalationEventDefinition',
      Conditional: 'bpmn:ConditionalEventDefinition',
      Compensation: 'bpmn:CompensateEventDefinition',
      Cancel: 'bpmn:CancelEventDefinition',
      Link: 'bpmn:LinkEventDefinition',
      Terminate: 'bpmn:TerminateEventDefinition',
    };

    const defType = TRIGGER_DEF_MAP[trigger];
    if (!defType) return;

    const defAttrs = {};

    // Add sub-attributes for specific event definition types
    switch (trigger) {
      case 'Timer':
        if (props.timeDuration) {
          defAttrs.timeDuration = this._moddle.create('bpmn:FormalExpression', { body: props.timeDuration });
        } else if (props.timeDate) {
          defAttrs.timeDate = this._moddle.create('bpmn:FormalExpression', { body: props.timeDate });
        } else if (props.timeCycle) {
          defAttrs.timeCycle = this._moddle.create('bpmn:FormalExpression', { body: props.timeCycle });
        }
        break;
      case 'Message':
        if (props.messageRef) defAttrs.messageRef = props.messageRef;
        break;
      case 'Signal':
        if (props.signalRef) defAttrs.signalRef = props.signalRef;
        break;
      case 'Error':
        if (props.errorRef) defAttrs.errorRef = props.errorRef;
        break;
      case 'Escalation':
        if (props.escalationRef) defAttrs.escalationRef = props.escalationRef;
        break;
      case 'Conditional':
        if (props.condition) {
          defAttrs.condition = this._moddle.create('bpmn:FormalExpression', { body: props.condition });
        }
        break;
      case 'Link':
        if (props.linkName) defAttrs.name = props.linkName;
        break;
    }

    const eventDef = this._moddle.create(defType, defAttrs);
    el.eventDefinitions = [eventDef];
  }

  _applyLoopCharacteristics(el, props) {
    const loopType = props.loopType;
    if (!loopType || loopType === 'None') return;

    if (loopType === 'Loop') {
      el.loopCharacteristics = this._moddle.create('bpmn:StandardLoopCharacteristics');
    } else if (loopType === 'SequentialMultiInstance') {
      el.loopCharacteristics = this._moddle.create('bpmn:MultiInstanceLoopCharacteristics', {
        isSequential: true,
      });
    } else if (loopType === 'ParallelMultiInstance') {
      el.loopCharacteristics = this._moddle.create('bpmn:MultiInstanceLoopCharacteristics', {
        isSequential: false,
      });
    }
  }

  _applyEdgeProperties(el, props) {
    // Call registered writer plugins for edge property serialization
    for (const plugin of this._writerPlugins) {
      if (typeof plugin.applyEdgeProperties === 'function') {
        plugin.applyEdgeProperties(el, props, this._moddle);
      }
    }
  }

  _ensureExtensionElements(el, extensions) {
    if (!el.extensionElements) {
      el.extensionElements = this._moddle.create('bpmn:ExtensionElements', { values: [] });
    }
    el.extensionElements.values.push(...extensions);
  }

  _buildDiagram(nodes, edges, elementsById, definitions) {
    const planeElement = [];

    for (const node of nodes) {
      let bpmnType;
      let sizes;

      if (node.type === 'epx:Participant') {
        bpmnType = 'bpmn:Participant';
        const props = node.properties || {};
        sizes = { width: props.width || 600, height: props.height || 250 };
      } else if (node.type === 'epx:Lane') {
        bpmnType = 'bpmn:Lane';
        const props = node.properties || {};
        sizes = { width: props.width || 570, height: props.height || 125 };
      } else {
        bpmnType = BPMN_TYPE_MAP[node.type];
        sizes = SHAPE_SIZES[bpmnType] || { width: 120, height: 60 };
      }

      const bounds = this._moddle.create('dc:Bounds', {
        x: node.x - sizes.width / 2,
        y: node.y - sizes.height / 2,
        width: sizes.width,
        height: sizes.height,
      });

      const shapeAttrs = {
        id: `${node.id}_di`,
        bpmnElement: elementsById[node.id],
        bounds,
      };

      // Mark expanded participants
      if (node.type === 'epx:Participant') {
        shapeAttrs.isExpanded = node.properties?.isExpanded !== false;
        shapeAttrs.isHorizontal = true;
      }

      const shape = this._moddle.create('bpmndi:BPMNShape', shapeAttrs);

      // Persist label position for nodes that use a LogicFlow text overlay
      // (events and gateways — tasks/pools/lanes render text inside SVG)
      if (node.text && node.text.value && typeof node.text.x === 'number' && typeof node.text.y === 'number') {
        const { width: lw, height: lh } = estimateLabelBounds(node.text.value, 150);
        shape.label = this._moddle.create('bpmndi:BPMNLabel', {
          bounds: this._moddle.create('dc:Bounds', {
            x: node.text.x - lw / 2,
            y: node.text.y - lh / 2,
            width: lw,
            height: lh,
          }),
        });
      }

      planeElement.push(shape);
    }

    for (const edge of edges) {
      const bpmnEdge = this._moddle.create('bpmndi:BPMNEdge', {
        id: `${edge.id}_di`,
        bpmnElement: elementsById[edge.id],
      });

      if (edge.pointsList && edge.pointsList.length) {
        bpmnEdge.waypoint = edge.pointsList.map(p =>
          this._moddle.create('dc:Point', { x: p.x, y: p.y })
        );
      }

      // Persist edge label position (always written so the above-line offset
      // from getTextPosition() is preserved on round-trip load)
      const edgeTextVal = edge.text && (typeof edge.text === 'string' ? edge.text : edge.text.value);
      if (edgeTextVal && typeof edge.text.x === 'number' && typeof edge.text.y === 'number') {
        const { width: lw, height: lh } = estimateLabelBounds(edgeTextVal, 100);
        bpmnEdge.label = this._moddle.create('bpmndi:BPMNLabel', {
          bounds: this._moddle.create('dc:Bounds', {
            x: edge.text.x - lw / 2,
            y: edge.text.y - lh / 2,
            width: lw,
            height: lh,
          }),
        });
      }

      planeElement.push(bpmnEdge);
    }

    // For collaboration, the plane's bpmnElement should be the collaboration
    let planeRef = definitions.rootElements[0];

    const plane = this._moddle.create('bpmndi:BPMNPlane', {
      id: 'BPMNPlane_1',
      bpmnElement: planeRef,
      planeElement,
    });

    return this._moddle.create('bpmndi:BPMNDiagram', {
      id: 'BPMNDiagram_1',
      plane,
    });
  }
}
